import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Badge, Tooltip, Group, Text, Box } from '@mantine/core';
import { Eye, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { getStatusColor, getStatusLabel, STATUS_FILTER_OPTIONS } from '../../constants/status';
import { formatDateBR } from '../../utils/formatters';
import PermissionGate from '../PermissionGate';

// Helper to extract color name from Tailwind classes for Mantine Badge
const mapTailwindToMantineColor = (tailwindClass: string): string => {
  if (tailwindClass.includes('green') || tailwindClass.includes('emerald')) return 'green';
  if (tailwindClass.includes('blue') || tailwindClass.includes('sky')) return 'blue';
  if (tailwindClass.includes('cyan')) return 'cyan';
  if (tailwindClass.includes('yellow') || tailwindClass.includes('amber')) return 'yellow';
  if (tailwindClass.includes('orange')) return 'orange';
  if (tailwindClass.includes('red') || tailwindClass.includes('rose')) return 'red';
  if (tailwindClass.includes('purple') || tailwindClass.includes('violet')) return 'violet';
  if (tailwindClass.includes('gray') || tailwindClass.includes('slate')) return 'gray';
  return 'blue'; // default
};

interface ContagensTableProps {
  jobs: any[];
  onEdit: (job: any) => void;
  onDelete: (id: number) => void;
  // Story 2.60: callback opcional pra restaurar (aba Excluidas)
  // Quando passado, o icone Delete vira Restore (mostrado se !row.ativo).
  onRestore?: (id: number) => void;
}

export const ContagensTable: React.FC<ContagensTableProps> = ({
  jobs,
  onEdit,
  onDelete,
  onRestore,
}) => {
  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Nº',
        size: 90,
        Cell: ({ row }) => (
          <Group gap={4} wrap="nowrap">
            <Text size="sm" fw={700} className="text-blue-600 dark:text-blue-400">
              {row.original.id}
            </Text>
            {/* Story 2.60: badge visual na lixeira */}
            {row.original.ativo === false && (
              <Badge color="red" size="xs" variant="filled" title="Contagem excluída">
                🗑️
              </Badge>
            )}
          </Group>
        ),
        enableColumnFilter: true,
        filterVariant: 'text',
      },
      {
        accessorKey: 'hospital_nome',
        header: 'Hospital',
        size: 250, // Fixed width for better density
        Cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {row.original.hospital_nome}
            </span>
            {row.original.produto_atribuido && (
              <Badge color="green" size="xs" variant="light" className="mt-0.5 w-fit p-0 h-4">
                ✅
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'clientes',
        header: 'Clientes',
        size: 200,
        accessorFn: (row) => {
            if (row.clientes && row.clientes.length > 0) {
                return row.clientes.map((c: any) => c.nome_fantasia || c.razao_social).join(', ');
            }
            return row.cliente_nome_fantasia || row.cliente_razao_social || '-';
        },
        Cell: ({ cell }) => (
          <Tooltip label={cell.getValue<string>()} withArrow>
            <Text size="sm" truncate="end">
              {cell.getValue<string>()}
            </Text>
          </Tooltip>
        ),
      },
      {
        id: 'auditores',
        header: 'Auditores',
        size: 180,
        accessorFn: (row) => {
            if (row.auditores && row.auditores.length > 0) {
                return row.auditores.map((a: any) => a.nome).join(', ');
            }
            return row.operador_nome || '';
        },
        Cell: ({ cell }) => (
          <Tooltip label={cell.getValue<string>()} withArrow>
            <Text size="sm" truncate="end">
              {cell.getValue<string>()}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'data_prevista',
        header: 'Data',
        size: 100,
        accessorFn: (row) => row.data_prevista,
        Cell: ({ cell }) => {
            const dateStr = cell.getValue<string>();
            return (
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                {formatDateBR(dateStr)}
              </Text>
            );
        },
        filterVariant: 'date',
        mantineFilterDateInputProps: {
            locale: 'pt-br',
        },
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        Cell: ({ row }) => {
          const status = row.original.status;
          const colorClass = getStatusColor(status);
          const label = getStatusLabel(status);
          return (
            <Badge color={mapTailwindToMantineColor(colorClass)} variant="light" size="sm">
              {label}
            </Badge>
          );
        },
        filterVariant: 'select',
        filterSelectOptions: STATUS_FILTER_OPTIONS.map(opt => ({ label: opt.label, value: opt.value })),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: jobs,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: { 
      showColumnFilters: true, 
      density: 'xs', // Compact density by default
    },
    // UI/UX Improvements for compact view
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': {
            padding: '8px 12px !important', // Force smaller header padding
        },
        'td': {
            padding: '4px 12px !important', // Force smaller cell padding
        }
      }
    },
    renderRowActions: ({ row }) => (
      <Group gap={4} wrap="nowrap">
        <ActionIcon
          onClick={() => navigate(`/cloud/contagens/${row.original.id}`)}
          variant="subtle"
          color="gray"
          size="sm"
          title="Ver detalhes"
        >
          <Eye size={14} />
        </ActionIcon>
        {/* Story 2.60: Restaurar (aba Excluidas) OU Deletar (aba Ativas) */}
        {onRestore && !row.original.ativo ? (
          <PermissionGate tela="CONTAGENS_LIST" acao="editar">
            <ActionIcon
              onClick={() => onRestore(row.original.id)}
              variant="subtle"
              color="green"
              size="sm"
              title="Restaurar"
            >
              <RotateCcw size={14} />
            </ActionIcon>
          </PermissionGate>
        ) : (
          <PermissionGate tela="CONTAGENS_LIST" acao="excluir">
            <ActionIcon
              onClick={() => onDelete(row.original.id)}
              variant="subtle"
              color="red"
              size="sm"
              title="Deletar"
            >
              <Trash2 size={14} />
            </ActionIcon>
          </PermissionGate>
        )}
      </Group>
    ),
    mantinePaperProps: {
        shadow: 'none',
        withBorder: true,
        radius: 'md',
    },
    localization: {
        actions: 'Ações',
        and: 'e',
        cancel: 'Cancelar',
        changeFilterMode: 'Alterar modo de filtro',
        changeSearchMode: 'Alterar modo de busca',
        clearFilter: 'Limpar filtro',
        clearSearch: 'Limpar busca',
        clearSort: 'Limpar ordenação',
        clickToCopy: 'Clique para copiar',
        collapse: 'Recolher',
        collapseAll: 'Recolher tudo',
        columnActions: 'Ações da coluna',
        copiedToClipboard: 'Copiado para a área de transferência',
        dropToGroupBy: 'Solte para agrupar por {column}',
        edit: 'Editar',
        expand: 'Expandir',
        expandAll: 'Expandir tudo',
        filterArrIncludes: 'Inclui',
        filterArrIncludesAll: 'Inclui todos',
        filterArrIncludesSome: 'Inclui alguns',
        filterBetween: 'Entre',
        filterBetweenInclusive: 'Entre inclusivo',
        filterByColumn: 'Filtrar por {column}',
        filterContains: 'Contém',
        filterEmpty: 'Vazio',
        filterEndsWith: 'Termina com',
        filterEquals: 'Igual',
        filterEqualsString: 'Igual',
        filterFuzzy: 'Aproximado',
        filterGreaterThan: 'Maior que',
        filterGreaterThanOrEqualTo: 'Maior ou igual a',
        filterInNumberRange: 'Entre',
        filterIncludesString: 'Contém',
        filterIncludesStringSensitive: 'Contém (SENSITIVE)',
        filterLessThan: 'Menor que',
        filterLessThanOrEqualTo: 'Menor ou igual a',
        filterMode: 'Modo de filtro: {filterType}',
        filterNotEmpty: 'Não vazio',
        filterNotEquals: 'Diferente',
        filterStartsWith: 'Começa com',
        filterWeakEquals: 'Igual',
        filteringByColumn: 'Filtrando por {column} - {filterType} {filterValue}',
        goToFirstPage: 'Ir para a primeira página',
        goToLastPage: 'Ir para a última página',
        goToNextPage: 'Ir para a próxima página',
        goToPreviousPage: 'Ir para a página anterior',
        grab: 'Agarra',
        groupByColumn: 'Agrupar por {column}',
        groupedBy: 'Agrupado por ',
        hideAll: 'Ocultar tudo',
        hideColumn: 'Ocultar coluna {column}',
        max: 'Máx',
        min: 'Mín',
        move: 'Mover',
        noRecordsToDisplay: 'Nenhum registro para exibir',
        noResultsFound: 'Nenhum resultado encontrado',
        of: 'de',
        or: 'ou',
        pinToLeft: 'Fixar à esquerda',
        pinToRight: 'Fixar à direita',
        resetColumnSize: 'Redefinir tamanho da coluna',
        resetOrder: 'Redefinir ordem',
        rowActions: 'Ações da linha',
        rowNumber: '#',
        rowNumbers: 'Números das linhas',
        rowsPerPage: 'Linhas por página',
        save: 'Salvar',
        search: 'Buscar',
        select: 'Selecionar',
        selectedCountOfRowCountRowsSelected: '{selectedCount} de {rowCount} linha(s) selecionada(s)',
        showAll: 'Mostrar tudo',
        showAllColumns: 'Mostrar todas as colunas',
        showHideColumns: 'Mostrar/Ocultar colunas',
        showHideFilters: 'Mostrar/Ocultar filtros',
        showHideSearch: 'Mostrar/Ocultar busca',
        sortByColumnAsc: 'Ordenar por {column} ascendente',
        sortByColumnDesc: 'Ordenar por {column} descendente',
        sortedByColumnAsc: 'Ordenado por {column} ascendente',
        sortedByColumnDesc: 'Ordenado por {column} descendente',
        thenBy: ', então por ',
        toggleDensity: 'Alternar densidade',
        toggleFullScreen: 'Alternar tela cheia',
        toggleSelectAll: 'Alternar selecionar tudo',
        toggleSelectRow: 'Alternar selecionar linha',
        toggleVisibility: 'Alternar visibilidade',
        ungroupByColumn: 'Desagrupar por {column}',
        unpin: 'Desafixar',
        unpinAll: 'Desafixar tudo',
        unsorted: 'Não ordenado',
    }
  });

  return <MantineReactTable table={table} />;
};